// src/Components/Modules/HR/HrDashboard.js
import React, { useEffect, useState, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge as MuiBadge,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  FaUsers,
  FaClock,
  FaCalendarAlt,
  FaMoneyCheck,
  FaChartLine,
  FaFileAlt,
  FaUserTie,
  FaBriefcase,
  FaCalendarCheck,
  FaCalendarTimes,
  FaMoneyBillWave,
  FaBell,
  FaGift,
  FaTrophy,
  FaClipboardList,
  FaRobot,
  FaArrowRight,
  FaArrowUp,
  FaArrowDown,
  FaSync,
  FaEye,
  FaPlus,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationCircle,
  FaStar,
  FaHeart,
  FaEnvelope,
  FaPhone,
  FaBuilding,
  FaMapMarkerAlt,
  FaCog,
  FaSignOutAlt,
  FaThumbsUp,
  FaThumbsDown,
  FaFire,
  FaRocket,
  FaAward,
  FaMedal,
  FaCrown,
} from "react-icons/fa";
import { styled, keyframes } from "@mui/material/styles";

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "400px",
    height: "400px",
    background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
    borderRadius: "50%",
    transform: "translate(30%, -30%)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "300px",
    height: "300px",
    background: "radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)",
    borderRadius: "50%",
    transform: "translate(-30%, 30%)",
  },
}));

const StatCard = styled(Card)(({ theme, color }) => ({
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  height: "100%",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s ease",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "150px",
    height: "150px",
    background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
    borderRadius: "50%",
    transform: "translate(30%, -30%)",
  },
}));

const QuickActionCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  transition: "all 0.3s ease",
  cursor: "pointer",
  border: "1px solid rgba(0,0,0,0.08)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 25px rgba(102, 126, 234, 0.2)",
    borderColor: "#667eea",
  },
}));

const ActivityItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  transition: "background 0.2s ease",
  "&:hover": {
    background: "#f8f9ff",
  },
}));

const HrDashboard = () => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [leaveSummary, setLeaveSummary] = useState({});
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const currentUser = {
    name: "John Doe",
    role: "HR Manager",
    avatar: "JD",
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [attRes, empRes, leaveRes] = await Promise.all([
        AxiosAuthInstance.get("hr/attendance/summary").catch(() => ({ data: {} })),
        AxiosAuthInstance.get("add_employee").catch(() => ({ data: { employee: [] } })),
        AxiosAuthInstance.get("hr/leave/pending").catch(() => ({ data: [] })),
      ]);

      const employees = empRes.data.employee || [];
      const attendance = attRes.data || {};
      const pendingLeaves = Array.isArray(leaveRes.data) ? leaveRes.data : [];

      setStats({
        totalEmp: employees.length,
        present: attendance.present || 0,
        late: attendance.late || 0,
        absent: attendance.absent || 0,
        pendingLeave: pendingLeaves.length,
        totalLeaves: attendance.total || employees.length,
      });

      // Generate mock attendance data for chart
      const mockAttendanceData = [
        { day: "Mon", present: 45, late: 5, absent: 3 },
        { day: "Tue", present: 48, late: 3, absent: 2 },
        { day: "Wed", present: 42, late: 7, absent: 4 },
        { day: "Thu", present: 46, late: 4, absent: 3 },
        { day: "Fri", present: 44, late: 6, absent: 3 },
        { day: "Sat", present: 38, late: 2, absent: 5 },
        { day: "Sun", present: 15, late: 1, absent: 8 },
      ];
      setAttendanceData(mockAttendanceData);

      // Mock recent activities
      const mockActivities = [
        {
          id: 1,
          type: "leave",
          user: "Sarah Johnson",
          action: "requested leave",
          time: "2 hours ago",
          icon: FaCalendarAlt,
          color: "#2196f3",
        },
        {
          id: 2,
          type: "attendance",
          user: "Mike Peters",
          action: "checked in late",
          time: "3 hours ago",
          icon: FaClock,
          color: "#ff9800",
        },
        {
          id: 3,
          type: "payroll",
          user: "System",
          action: "payroll processed",
          time: "5 hours ago",
          icon: FaMoneyBillWave,
          color: "#4caf50",
        },
        {
          id: 4,
          type: "employee",
          user: "Emily Davis",
          action: "joined the company",
          time: "1 day ago",
          icon: FaUserTie,
          color: "#9c27b0",
        },
      ];
      setRecentActivities(mockActivities);

      // Mock upcoming events
      const mockEvents = [
        {
          id: 1,
          title: "Team Meeting",
          date: "Today, 2:00 PM",
          type: "meeting",
          icon: FaUsers,
          color: "#2196f3",
        },
        {
          id: 2,
          title: "Sarah's Birthday",
          date: "Tomorrow",
          type: "birthday",
          icon: FaGift,
          color: "#e91e63",
        },
        {
          id: 3,
          title: "Performance Review",
          date: "Dec 20, 10:00 AM",
          type: "review",
          icon: FaTrophy,
          color: "#ff9800",
        },
      ];
      setUpcomingEvents(mockEvents);

      setLeaveSummary({
        pending: pendingLeaves.length,
        approved: 12,
        rejected: 3,
      });
    } catch (err) {
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: "Apply Leave",
      icon: FaCalendarAlt,
      color: "#2196f3",
      link: "/hr/leave/apply",
      description: "Request time off",
    },
    {
      title: "Expense Claim",
      icon: FaFileAlt,
      color: "#4caf50",
      link: "/hr/expenses",
      description: "Submit expenses",
    },
    {
      title: "AI Assistant",
      icon: FaRobot,
      color: "#9c27b0",
      link: "/hr/ai-chat",
      description: "Get HR help",
    },
    {
      title: "Performance",
      icon: FaTrophy,
      color: "#ff9800",
      link: "/hr/performance",
      description: "View reviews",
    },
    {
      title: "Attendance",
      icon: FaClock,
      color: "#00bcd4",
      link: "/hr/attendance",
      description: "Check attendance",
    },
    {
      title: "Payroll",
      icon: FaMoneyCheck,
      color: "#f44336",
      link: "/hr/payroll",
      description: "View payroll",
    },
  ];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const attendancePercentage = useMemo(() => {
    if (stats.totalEmp === 0) return 0;
    return Math.round((stats.present / stats.totalEmp) * 100);
  }, [stats]);

  if (loading) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="HR Dashboard" />
        <GradientPaper>
          <Skeleton variant="text" width="40%" height={50} />
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="50%" />
        </GradientPaper>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="HR Dashboard" />

      {/* Welcome Header */}
      <GradientPaper elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "center", md: "flex-start" },
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
            <Typography variant="h3" fontWeight={700} gutterBottom>
              {getGreeting()}, {currentUser.name}! 👋
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.95, mb: 1 }}>
              {currentUser.role}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {getCurrentDate()}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh Data">
              <IconButton
                onClick={loadDashboardData}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <FaSync />
              </IconButton>
            </Tooltip>
            <Tooltip title="Notifications">
              <IconButton
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <MuiBadge badgeContent={stats.pendingLeave} color="error">
                  <FaBell />
                </MuiBadge>
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#667eea" elevation={0}>
            <CardContent sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    bgcolor: "#e3f2fd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaUsers size={28} color="#2196f3" />
                </Box>
                <Chip
                  icon={<FaArrowUp />}
                  label="+5%"
                  size="small"
                  sx={{
                    bgcolor: "#e8f5e9",
                    color: "#2e7d32",
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Typography variant="h3" fontWeight={700} color="#1565c0" sx={{ mb: 0.5 }}>
                {stats.totalEmp}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Total Employees
              </Typography>
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "rgba(33, 150, 243, 0.1)",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "#2196f3",
                    },
                  }}
                />
              </Box>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#4caf50" elevation={0}>
            <CardContent sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    bgcolor: "#e8f5e9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaCheckCircle size={28} color="#4caf50" />
                </Box>
                <Chip
                  label={`${attendancePercentage}%`}
                  size="small"
                  sx={{
                    bgcolor: "#e8f5e9",
                    color: "#2e7d32",
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Typography variant="h3" fontWeight={700} color="#2e7d32" sx={{ mb: 0.5 }}>
                {stats.present}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Present Today
              </Typography>
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={attendancePercentage}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "rgba(76, 175, 80, 0.1)",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "#4caf50",
                    },
                  }}
                />
              </Box>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#ff9800" elevation={0}>
            <CardContent sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    bgcolor: "#fff3e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaClock size={28} color="#ff9800" />
                </Box>
                <Chip
                  icon={<FaArrowDown />}
                  label="-2%"
                  size="small"
                  sx={{
                    bgcolor: "#ffebee",
                    color: "#c62828",
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Typography variant="h3" fontWeight={700} color="#ef6c00" sx={{ mb: 0.5 }}>
                {stats.late}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Late Today
              </Typography>
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={stats.totalEmp > 0 ? (stats.late / stats.totalEmp) * 100 : 0}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "rgba(255, 152, 0, 0.1)",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "#ff9800",
                    },
                  }}
                />
              </Box>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#9c27b0" elevation={0}>
            <CardContent sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    bgcolor: "#f3e5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaCalendarAlt size={28} color="#9c27b0" />
                </Box>
                <MuiBadge badgeContent={stats.pendingLeave} color="error">
                  <Chip
                    label="Pending"
                    size="small"
                    sx={{
                      bgcolor: "#f3e5f5",
                      color: "#7b1fa2",
                      fontWeight: 600,
                    }}
                  />
                </MuiBadge>
              </Box>
              <Typography variant="h3" fontWeight={700} color="#7b1fa2" sx={{ mb: 0.5 }}>
                {stats.pendingLeave}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Pending Leaves
              </Typography>
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={stats.totalEmp > 0 ? (stats.pendingLeave / stats.totalEmp) * 100 : 0}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "rgba(156, 39, 176, 0.1)",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "#9c27b0",
                    },
                  }}
                />
              </Box>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {/* Charts and Activity Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Attendance Chart */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  <FaChartLine style={{ marginRight: 8, verticalAlign: "middle", color: "#667eea" }} />
                  Weekly Attendance Overview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track attendance trends for the current week
                </Typography>
              </Box>
              <Chip
                label="This Week"
                size="small"
                sx={{
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                  fontWeight: 600,
                }}
              />
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={attendanceData}>
                <defs>
                  <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#4caf50" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorLate" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff9800" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#ff9800" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorAbsent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f44336" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#f44336" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" stroke="#757575" />
                <YAxis stroke="#757575" />
                <RechartsTooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="present"
                  stroke="#4caf50"
                  fillOpacity={1}
                  fill="url(#colorPresent)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="late"
                  stroke="#ff9800"
                  fillOpacity={1}
                  fill="url(#colorLate)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="absent"
                  stroke="#f44336"
                  fillOpacity={1}
                  fill="url(#colorAbsent)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Recent Activities */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", height: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                <FaClipboardList style={{ marginRight: 8, verticalAlign: "middle", color: "#667eea" }} />
                Recent Activities
              </Typography>
              <Button size="small" endIcon={<FaArrowRight />}>
                View All
              </Button>
            </Box>
            <Stack spacing={1}>
              {recentActivities.map((activity) => {
                const IconComponent = activity.icon;
                return (
                  <ActivityItem key={activity.id}>
                    <Avatar
                      sx={{
                        bgcolor: `${activity.color}20`,
                        color: activity.color,
                        width: 40,
                        height: 40,
                      }}
                    >
                      <IconComponent />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {activity.user}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {activity.action}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {activity.time}
                      </Typography>
                    </Box>
                  </ActivityItem>
                );
              })}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions & Upcoming Events */}
      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
              <FaRocket style={{ marginRight: 8, verticalAlign: "middle", color: "#667eea" }} />
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              {quickActions.map((action, index) => {
                const IconComponent = action.icon;
                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <QuickActionCard
                      elevation={0}
                      onClick={() => navigate(action.link)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: "50%",
                            bgcolor: `${action.color}20`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: action.color,
                          }}
                        >
                          <IconComponent size={24} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {action.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {action.description}
                          </Typography>
                        </Box>
                      </Box>
                    </QuickActionCard>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>

        {/* Upcoming Events */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                <FaCalendarAlt style={{ marginRight: 8, verticalAlign: "middle", color: "#667eea" }} />
                Upcoming Events
              </Typography>
            </Box>
            <Stack spacing={2}>
              {upcomingEvents.map((event) => {
                const IconComponent = event.icon;
                return (
                  <Box
                    key={event.id}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid rgba(0,0,0,0.08)",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: event.color,
                        bgcolor: `${event.color}05`,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: `${event.color}20`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: event.color,
                        }}
                      >
                        <IconComponent />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {event.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {event.date}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default HrDashboard;